/* eslint-disable react/jsx-no-bind */
import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';

import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import InputBase from '@mui/material/InputBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { erc20Abi, formatEther, parseUnits } from 'viem';
import { usePublicClient, useConnection } from 'wagmi';

import Iconify from 'src/components/iconify';
import { AuthContext } from 'src/auth/context';
import { useEthereum } from 'src/hooks/useEthereum';
import { useBoolean } from 'src/hooks/useBoolean';
import { useDebounce } from 'src/hooks/useDebounce';
import { useDeposit } from 'src/hooks/useDeposit';
import { useTokenOneMax, ETH_DEPOSIT_GAS_UNITS, getMaxFeePerGas } from 'src/hooks/useTokenOneMax';
import { useWalletSession } from 'src/hooks/useWalletSession';
import { useSelector } from 'src/redux/hooks';
import WalletConnectButtonClient from 'src/components/wallet-connect/WalletConnectButton-client';
import { lazyRetry } from 'src/utils/lazyRetry';

import { getTokenList } from 'src/utils/tokenList';
import { getActiveAddresses } from 'src/config/contract-loader';
import { getTargetName } from 'src/utils/crypto/networks';
import { handleContractError } from 'src/utils/crypto/handleContractError';
import { cryptoStrategy } from 'src/features/transfer/strategies/cryptoStrategy';
import { createExchangePayoutIntent } from 'src/services/createExchangePayoutIntent';
import {
  formatAmount,
  formatFromAtomic,
  parseToAtomic,
  quoteAmountAtomicFE,
} from 'src/utils/formatNumber';
import { axiosInstanceNew } from 'src/utils/axios';
import { clientDispatch } from 'src/redux/clientStore';
import { contractBalanceUpdated } from 'src/redux/slices/contract-balance-slice';
import { fetchProfitPercent } from 'src/redux/slices/client-profit-slice';

import { UI, EMPTY } from '../constants';
import { gradientButtonSx, fieldBoxSx, cardSx, getTabSx, getSegmentSx } from '../styles';
import { quoteFundWallet } from '../lib/quote';
import { validateDestination } from '../lib/validate';
import { shortenAddress, formatUsd, formatCryptoAmount, getIsPhoneSession } from '../lib/format';
import { MonoLabel, SelectorButton, QuoteRow } from '../components/atoms';
import { WhatsAppIcon } from '../components/icons';

const loadLoginModal = () => import('src/features/auth/loginModal');
const LoginModal = lazyRetry(loadLoginModal);

const FiatPayoutConfirmDialog = lazyRetry(
  () => import('src/components/payout/FiatPayoutConfirmDialog')
);

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
const SEND_SLIPPAGE = 2.5;

const SEND_PAYOUT_PROVIDERS = ['EVC Plus', 'Zaad', 'Sahal'];

// Gas units assumed for an ERC-20 (USDT) deposit in the pre-send gas check.
const ERC20_DEPOSIT_GAS_UNITS = 350_000n;

// ----------------------------------------------------------------------
// Phone helpers (match old SendUsdView: 252 + 9 digits, no '+')
// ----------------------------------------------------------------------
function normalizePhone(value) {
  return String(value || '')
    .replace(/\D/g, '')
    .slice(0, 9);
}

function formatSomaliPhone(value) {
  return `252${normalizePhone(value)}`;
}

const recipientPhoneSchema = yup.object().shape({
  recipientPhone: yup
    .string()
    .trim()
    .required('Recipient phone number is required.')
    .matches(/^\d{9}$/, 'Enter a valid 9-digit WhatsApp number.'),
});

// ----------------------------------------------------------------------
// Token helpers
// ----------------------------------------------------------------------
function isNativeEthToken(token) {
  const ticker = String(token?.ticker || '').toLowerCase();
  const address = String(token?.address || '').toLowerCase();
  return (
    token?.type === 'crypto' &&
    (ticker === 'eth' || ticker === 'ethereum' || address === ZERO_ADDRESS || !token?.address)
  );
}

function isUsdtInputToken(token) {
  return token?.type === 'crypto' && String(token?.ticker || '').toLowerCase() === 'usdt';
}

function parseTokenAmountSafe({ amount, decimals }) {
  try {
    if (!amount) return 0n;
    return parseUnits(String(amount), Number(decimals ?? 18));
  } catch (error) {
    return 0n;
  }
}

async function checkEthGasBalanceForCryptoDeposit({
  address,
  tokenOne,
  tokenOneAmount,
  publicClient,
}) {
  if (!publicClient || !address) {
    return {
      hasEnoughEthForGas: false,
      message: 'Wallet is not ready. Please reconnect your wallet.',
    };
  }
  if (tokenOne?.type !== 'crypto') {
    return { hasEnoughEthForGas: true };
  }

  const [ethBalance, feePerGas] = await Promise.all([
    publicClient.getBalance({ address }),
    getMaxFeePerGas(publicClient),
  ]);
  const isEthDeposit = isNativeEthToken(tokenOne);

  // ETH uses the same gas units as the MAX reserve in useTokenOneMax (which
  // reserves 150% of the fee), so a MAX amount always passes this 120% check.
  let estimatedGasUnits = ERC20_DEPOSIT_GAS_UNITS;
  if (isEthDeposit) {
    estimatedGasUnits = ETH_DEPOSIT_GAS_UNITS;
  }
  const estimatedGasCost = (estimatedGasUnits * feePerGas * 120n) / 100n;

  let depositAmountAtomic = 0n;
  if (isEthDeposit) {
    depositAmountAtomic = parseUnits(
      String(tokenOneAmount || '0'),
      Number(tokenOne?.decimals ?? 18)
    );
  }

  const requiredEth = depositAmountAtomic + estimatedGasCost;

  if (ethBalance < requiredEth) {
    const shortBy = requiredEth - ethBalance;

    let message = `You need ETH in your wallet to pay the network fee. Even though you are paying with ${tokenOne?.ticker}, the fee is paid in ETH. Add about ${formatEther(shortBy)} ETH to continue.`;
    if (isEthDeposit) {
      message = `You need more ETH to complete this payment. Your wallet must cover the amount you send plus the network fee — about ${formatEther(requiredEth)} ETH in total.`;
    }

    return { hasEnoughEthForGas: false, message };
  }

  return { hasEnoughEthForGas: true };
}

async function readUsdtAllowance({ address, tokenOne, chainId, publicClient }) {
  if (!publicClient || !address || !tokenOne || !chainId) return null;
  if (!isUsdtInputToken(tokenOne)) return null;

  const active = getActiveAddresses(chainId);
  const tokenAddress = active?.usdt;
  const bridgeAddress = active?.FiatCryptoBridge;

  if (!tokenAddress || !bridgeAddress) {
    throw new Error('Missing USDT or bridge address for the active network.');
  }

  return publicClient.readContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [address, bridgeAddress],
  });
}

function checkSendUserBalance({ tokenOne, tokenOneAmount, maxAmount, loadingMax }) {
  if (!tokenOne || tokenOne?.type === 'fiat') return { ok: true };
  if (loadingMax) return { ok: false, message: 'Checking wallet balance...' };
  if (!maxAmount) {
    return { ok: false, message: `Unable to verify ${tokenOne.ticker} balance right now.` };
  }

  try {
    const decimals = Number(tokenOne?.decimals ?? 18);
    const inputAtomic = parseUnits(String(tokenOneAmount ?? '0'), decimals);
    const maxAtomic = parseUnits(String(maxAmount ?? '0'), decimals);
    if (inputAtomic > maxAtomic) {
      return { ok: false, message: `Insufficient ${tokenOne.ticker} balance in your wallet.` };
    }
    return { ok: true };
  } catch (error) {
    return { ok: false, message: 'Invalid amount entered.' };
  }
}

// Detect the "quote expired" family of failures so we can clear the amount and
// let the user re-enter for a fresh quote.
function isExpiredQuoteMessage(message) {
  const text = String(message || '').toLowerCase();
  return (
    text.includes('quote expired') ||
    text.includes('about to expire') ||
    text.includes('quote is about') ||
    text.includes('refresh the quote') ||
    text.includes('refresh and try again')
  );
}

// Plain decimal string for the API (never exponent notation like 1e-7).
function toPlainAmountString(value, decimals) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '0';

  const places = Math.min(Math.max(Number(decimals) || 6, 0), 18);
  const fixed = n.toFixed(places);

  if (!fixed.includes('.')) return fixed;
  return fixed.replace(/0+$/, '').replace(/\.$/, '');
}

function newClientRequestId() {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  return `${Date.now()}-${Math.random()}`;
}

function formatFeePercent(profitLoaded, profitPercent, feeUsdValue) {
  if (!profitLoaded) return '—';

  const base = `${profitPercent}%`;
  if (feeUsdValue != null && feeUsdValue > 0) {
    return `${base} (~${formatUsd(feeUsdValue)})`;
  }

  return base;
}

function getMaxButtonSx(disabled) {
  if (disabled) {
    return {
      cursor: 'default',
      color: UI.textFaint,
      border: `1px solid ${UI.border}`,
      '&:hover': {},
    };
  }

  return {
    cursor: 'pointer',
    color: UI.green,
    border: '1px solid rgba(127,212,38,0.35)',
    '&:hover': { bgcolor: 'rgba(127,212,38,0.1)' },
  };
}

function getDestSegmentSx(active) {
  if (active) {
    return {
      fontWeight: 700,
      color: UI.green,
      '&:hover': { color: UI.green },
    };
  }

  return {
    fontWeight: 500,
    color: UI.textMuted,
    '&:hover': { color: UI.text },
  };
}

function getDestIndicatorTransform(destMode) {
  if (destMode === 'manual') return 'translateX(100%)';
  return 'translateX(0)';
}

export default function TransferView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { user, loading, prepareAuthRequests } = useContext(AuthContext);
  const { ensureWalletSession: ensureWalletSessionFor } = useWalletSession();

  const {
    connected,
    address,
    targetChainId,
    walletChainId,
    switchNetworkError,
    walletReady,
    isReconnecting,
    isConnecting,
    isSwitchingNetwork,
    isBusy,
    accountMismatch,
  } = useEthereum();

  const { connector: activeConnector } = useConnection();

  const publicClient = usePublicClient({ chainId: targetChainId });
  const pricesById = useSelector((state) => state.prices?.byId) || EMPTY;
  // null until /settings/profit-percent answers — never show or quote a
  // made-up fee in the meantime.
  const profitPercentRaw = useSelector((state) => state?.settings?.profitPercent);
  const profitLoaded = profitPercentRaw !== null && profitPercentRaw !== undefined;
  const profitPercent = Number(profitPercentRaw ?? 0);

  const isPhoneSession = getIsPhoneSession(user);
  const targetName = getTargetName();
  const userStatus = user?.status || '';

  // Real token objects for the active chain (USDT, ETH, USD)
  const chainTokens = useMemo(() => getTokenList(targetChainId), [targetChainId]);
  const usdtToken = useMemo(
    () => chainTokens.find((t) => isUsdtInputToken(t)) || null,
    [chainTokens]
  );
  const ethToken = useMemo(
    () => chainTokens.find((t) => isNativeEthToken(t)) || null,
    [chainTokens]
  );
  const usdToken = useMemo(() => chainTokens.find((t) => t.type === 'fiat') || null, [chainTokens]);
  const cryptoTokens = useMemo(() => chainTokens.filter((t) => t.type === 'crypto'), [chainTokens]);

  const [activeTab, setActiveTab] = useState('send');

  // ----- Send (crypto -> fiat), verify-first -----
  const [payWith, setPayWith] = useState('usdt');
  const [usdToSend, setUsdToSend] = useState('');
  const [tokenOneAmount, setTokenOneAmount] = useState('');
  const [depositing, setDepositing] = useState(false);
  const [postApprovalHint, setPostApprovalHint] = useState('');

  const [recipientCheckStatus, setRecipientCheckStatus] = useState('idle');
  const [recipientCheckMessage, setRecipientCheckMessage] = useState('');
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [recipientName, setRecipientName] = useState('');

  const [usdtAllowance, setUsdtAllowance] = useState(null);
  const [loadingAllowance, setLoadingAllowance] = useState(false);
  const [allowanceError, setAllowanceError] = useState('');
  const [allowanceRefreshNonce, setAllowanceRefreshNonce] = useState(0);

  // ----- Fund (fiat -> crypto) -----
  const [fundAmount, setFundAmount] = useState('');
  const [receiveAsset, setReceiveAsset] = useState(null);
  const [destMode, setDestMode] = useState('connect');
  const [manualAddress, setManualAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [, setFundAssetAnchor] = useState(null);

  // Fund payment confirm dialog
  const openFundDialog = useBoolean();
  const [fundPreview, setFundPreview] = useState(null);
  const [fundClickedAck, setFundClickedAck] = useState(false);
  const fundDestinationRef = useRef('');

  // Login modal (Fund)
  const [openLogin, setOpenLogin] = useState(false);
  const [hasOpenedLogin, setHasOpenedLogin] = useState(false);
  const [loginModalKey, setLoginModalKey] = useState(0);

  const swapInFlightRef = useRef(false);
  const { deposit, approveIfNeeded } = useDeposit();

  const debouncedUsd = useDebounce(usdToSend, 400);

  const { control, watch, trigger, getValues, resetField, clearErrors } = useForm({
    resolver: yupResolver(recipientPhoneSchema),
    defaultValues: { recipientPhone: '' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const recipientPhone = watch('recipientPhone') || '';

  const tokenOne = useMemo(() => {
    if (payWith === 'eth') return ethToken;
    return usdtToken;
  }, [payWith, ethToken, usdtToken]);
  const tokenTwo = usdToken;

  const isCryptoDepositToFiatPayout = tokenOne?.type === 'crypto' && tokenTwo?.type === 'fiat';

  // For ETH, maxAmount already has the live gas reserve subtracted.
  const { maxAmount, loadingMax } = useTokenOneMax({ publicClient, address, tokenOne });

  useEffect(() => {
    if (!receiveAsset && cryptoTokens[0]) setReceiveAsset(cryptoTokens[0]);
  }, [cryptoTokens, receiveAsset]);

  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      try {
        const [balanceRes] = await Promise.all([
          axiosInstanceNew.get('/tokens/contract-balance'),
          clientDispatch(fetchProfitPercent()),
        ]);
        if (ignore) return;
        if (balanceRes?.data) {
          const { eth, usdt, usd } = balanceRes.data;
          clientDispatch(contractBalanceUpdated({ eth, usdt, usd }));
        }
      } catch (err) {
        console.error('Failed to fetch initial swap data:', err);
      }
    };
    fetchData();
    return () => {
      ignore = true;
    };
  }, []);

  // ---- prices / rate -----------------------------------------------------
  const priceIn = useMemo(
    () => pricesById?.[tokenOne?.coingeckoId]?.usd,
    [pricesById, tokenOne?.coingeckoId]
  );
  const priceOut = useMemo(
    () => pricesById?.[tokenTwo?.coingeckoId]?.usd,
    [pricesById, tokenTwo?.coingeckoId]
  );

  const rate = useMemo(() => {
    if (!priceIn || !priceOut) return null;
    return priceIn / priceOut;
  }, [priceIn, priceOut]);

  const rateLoading = !rate || !profitLoaded;

  const feeRate = useMemo(() => {
    const pct = Number(profitPercent);
    if (!Number.isFinite(pct) || pct <= 0) return 0;
    return pct / 100;
  }, [profitPercent]);

  const computeCryptoFromUsd = useCallback(
    (usdValue) => {
      const usdNum = Number.parseFloat(String(usdValue || '').trim());
      if (!Number.isFinite(usdNum) || usdNum <= 0) return '';
      if (!priceIn || priceIn <= 0) return '';
      if (!profitLoaded) return '';

      const denom = 1 - feeRate;
      if (denom <= 0) return '';

      const grossUsd = usdNum / denom;
      const cryptoCharged = grossUsd / priceIn;
      if (!Number.isFinite(cryptoCharged) || cryptoCharged <= 0) return '';

      const decimals = Number(tokenOne?.decimals ?? 18);
      return cryptoCharged.toFixed(Math.min(decimals, 18));
    },
    [priceIn, feeRate, tokenOne?.decimals, profitLoaded]
  );

  useEffect(() => {
    const next = computeCryptoFromUsd(debouncedUsd);
    setTokenOneAmount((prev) => {
      if (prev === next) return prev;
      return next;
    });
  }, [debouncedUsd, computeCryptoFromUsd]);

  const hasInputAmount = Boolean(usdToSend) && Boolean(tokenOneAmount);
  const isWalletDisconnected = !connected;
  const isUsdtDeposit = isUsdtInputToken(tokenOne);

  const phoneValid = normalizePhone(recipientPhone).length === 9;
  const checkingRecipient = recipientCheckStatus === 'checking';
  const recipientVerified =
    recipientCheckStatus === 'verified' &&
    Boolean(verifiedPhone) &&
    verifiedPhone === recipientPhone;
  const recipientUnavailable = recipientCheckStatus === 'unavailable';
  const recipientErrored = recipientCheckStatus === 'error';
  const phoneLocked = recipientVerified;

  const isWrongNetwork = useMemo(
    () =>
      Boolean(connected && switchNetworkError && walletChainId != null && targetChainId != null),
    [connected, switchNetworkError, walletChainId, targetChainId]
  );

  const inputAmountAtomic = useMemo(
    () => parseTokenAmountSafe({ amount: tokenOneAmount, decimals: tokenOne?.decimals }),
    [tokenOneAmount, tokenOne?.decimals]
  );

  const exceedsBalance = useMemo(() => {
    if (!tokenOne || tokenOne?.type === 'fiat') return false;
    if (loadingMax || !maxAmount) return false;
    if (!tokenOneAmount) return false;
    try {
      const decimals = Number(tokenOne?.decimals ?? 18);
      const inputAtomic = parseUnits(String(tokenOneAmount), decimals);
      const maxAtomic = parseUnits(String(maxAmount), decimals);
      return inputAtomic > maxAtomic;
    } catch (error) {
      return false;
    }
  }, [tokenOne, loadingMax, maxAmount, tokenOneAmount]);

  const needsUsdtApproval = useMemo(() => {
    if (!isUsdtDeposit) return false;
    if (!hasInputAmount) return false;
    if (inputAmountAtomic <= 0n) return false;
    if (usdtAllowance === null || usdtAllowance === undefined) return false;
    try {
      return BigInt(usdtAllowance) < inputAmountAtomic;
    } catch (error) {
      return false;
    }
  }, [isUsdtDeposit, hasInputAmount, inputAmountAtomic, usdtAllowance]);

  const refreshUsdtAllowance = useCallback(async () => {
    setAllowanceError('');
    if (!connected || !address || !targetChainId || !isUsdtDeposit) {
      setUsdtAllowance(null);
      setLoadingAllowance(false);
      return null;
    }
    setLoadingAllowance(true);
    try {
      const allowance = await readUsdtAllowance({
        address,
        tokenOne,
        chainId: targetChainId,
        publicClient,
      });
      setUsdtAllowance(allowance);
      return allowance;
    } catch (err) {
      setAllowanceError(handleContractError(err) || 'Failed to check USDT approval.');
      setUsdtAllowance(null);
      return null;
    } finally {
      setLoadingAllowance(false);
    }
  }, [connected, address, targetChainId, isUsdtDeposit, tokenOne, publicClient]);

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      const allowance = await refreshUsdtAllowance();
      if (ignore) {
        // component/deps changed while the read was in flight; the newer
        // effect run owns the state, so we intentionally drop this result.
      }
      return allowance;
    };
    run();
    return () => {
      ignore = true;
    };
  }, [refreshUsdtAllowance, allowanceRefreshNonce]);

  const usdOut = useMemo(() => {
    const n = Number.parseFloat(String(usdToSend || '').trim());
    if (!Number.isFinite(n) || n <= 0) return '';
    return n.toFixed(2);
  }, [usdToSend]);

  const NETWORK_FEE_USD_ATOMIC = useMemo(() => parseToAtomic('0', 6), []);

  const usdOutNum = Number.parseFloat(String(usdOut || '').trim());
  const isUsdOutInvalid = useMemo(() => {
    if (!usdToSend) return false;
    return !Number.isFinite(usdOutNum) || usdOutNum <= 0;
  }, [usdToSend, usdOutNum]);

  const feeUsd = useMemo(() => {
    if (!usdOutNum || feeRate <= 0) return null;
    const denom = 1 - feeRate;
    if (denom <= 0) return null;
    const grossUsd = usdOutNum / denom;
    return grossUsd - usdOutNum;
  }, [usdOutNum, feeRate]);

  // ---- Fund quote -------------------------------------------------------
  const fundQuote = useMemo(() => {
    if (!profitLoaded) return null;

    return quoteFundWallet({
      amount: fundAmount,
      asset: { coingeckoId: receiveAsset?.coingeckoId, label: receiveAsset?.ticker },
      byId: pricesById,
      profitPercent,
    });
  }, [fundAmount, receiveAsset, pricesById, profitPercent, profitLoaded]);

  // ---- helpers -----------------------------------------------------------
  const resetSwapInput = useCallback(() => {
    setUsdToSend('');
    setTokenOneAmount('');
    setUsdtAllowance(null);
    setAllowanceError('');
    setPostApprovalHint('');
  }, []);

  const depositForStrategy = useCallback(
    (payload) =>
      deposit({
        ...payload,
        precheckedAllowance: usdtAllowance?.toString?.() || null,
        skipApproval: isUsdtInputToken(payload?.tokenOne),
      }),
    [deposit, usdtAllowance]
  );

  // Skips the claim entirely when the session already belongs to this wallet,
  // and never tears down the live socket (the old version disconnected and
  // reconnected it on every Check / Send, dropping live updates on mobile).
  const ensureWalletSession = useCallback(
    () => ensureWalletSessionFor(address),
    [ensureWalletSessionFor, address]
  );

  const assertLiveProvider = useCallback(async () => {
    if (user?.sessionAuthType === 'phone') return true;

    if (!activeConnector || typeof activeConnector.getProvider !== 'function') {
      toast.error('Wallet connection was lost. Please reconnect MetaMask and try again.');
      return false;
    }

    try {
      const provider = await activeConnector.getProvider();

      if (!provider) {
        toast.error('Wallet connection was lost. Please reconnect MetaMask and try again.');
        return false;
      }

      const accounts = await provider.request({ method: 'eth_accounts' });
      let activeAccount = null;
      if (Array.isArray(accounts)) {
        [activeAccount] = accounts;
      }

      if (!activeAccount) {
        toast.error('MetaMask is locked or disconnected. Reopen MetaMask, then try again.');
        return false;
      }

      if (address && activeAccount.toLowerCase() !== String(address).toLowerCase()) {
        toast.error('The active MetaMask account changed. Reconnect, then try again.');
        return false;
      }

      return true;
    } catch (error) {
      console.error('[assertLiveProvider failed]', error);
      toast.error('Could not reach MetaMask. Reopen the app and try again.');
      return false;
    }
  }, [activeConnector, address, user?.sessionAuthType]);

  const handleChangeRecipient = useCallback(() => {
    setRecipientCheckStatus('idle');
    setRecipientCheckMessage('');
    setVerifiedPhone('');
    setRecipientName('');
    resetSwapInput();
  }, [resetSwapInput]);

  const handleVerifyRecipient = useCallback(async () => {
    if (userStatus === 'blocked') {
      setRecipientCheckStatus('error');
      setRecipientCheckMessage('Your account is temporarily blocked. Please contact support.');
      setVerifiedPhone('');
      setRecipientName('');
      toast.error('Your account is temporarily blocked. Please contact support.');
      return;
    }

    const isValid = await trigger('recipientPhone', { shouldFocus: true });
    if (!isValid) {
      setRecipientCheckStatus('error');
      setRecipientCheckMessage('Enter a valid 9-digit WhatsApp number.');
      setVerifiedPhone('');
      setRecipientName('');
      toast.error('Enter a valid 9-digit WhatsApp number.');
      return;
    }

    const currentPhone = getValues('recipientPhone');

    setRecipientCheckStatus('checking');
    setRecipientCheckMessage('');
    setVerifiedPhone('');
    setRecipientName('');

    try {
      const sessionReady = await ensureWalletSession();
      if (!sessionReady) {
        setRecipientCheckStatus('error');
        setRecipientCheckMessage('Please connect MetaMask first.');
        setVerifiedPhone('');
        return;
      }

      const response = await axiosInstanceNew.post('/fiat/verify-recipient', {
        phoneNumber: formatSomaliPhone(currentPhone),
      });

      const data = response?.data;

      if (data?.success && data?.available) {
        const message = data?.message || 'Recipient number verified.';
        setRecipientName(data?.name || '');
        setVerifiedPhone(currentPhone);
        setRecipientCheckStatus('verified');
        setRecipientCheckMessage(message);
        toast.success(message);
        return;
      }

      const message = data?.message || 'This recipient number was not found.';
      setRecipientCheckStatus('unavailable');
      setRecipientCheckMessage(message);
      setVerifiedPhone('');
      toast.error(message);
    } catch (error) {
      console.error('[verify-recipient frontend failed]', {
        status: error?.response?.status,
        data: error?.response?.data,
        code: error?.code,
        message: error?.message,
      });

      let message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Could not verify this number right now.';

      if (error?.code === 'ECONNABORTED') {
        message = 'Verification took too long. Please try again.';
      }

      setRecipientCheckStatus('error');
      setRecipientCheckMessage(message);
      setVerifiedPhone('');
      toast.error(message);
    }
  }, [userStatus, trigger, getValues, ensureWalletSession]);

  const isSendDisabled = useMemo(() => {
    if (depositing || isReconnecting || isConnecting || isSwitchingNetwork || isBusy) return true;
    if (!phoneValid || !recipientVerified) return true;
    if (connected && accountMismatch) return true;
    if (isWalletDisconnected) return false;
    if (isWrongNetwork) return false;
    return (
      !hasInputAmount ||
      exceedsBalance ||
      loadingAllowance ||
      swapInFlightRef.current ||
      isUsdOutInvalid ||
      !walletReady
    );
  }, [
    depositing,
    isReconnecting,
    isConnecting,
    isSwitchingNetwork,
    isBusy,
    phoneValid,
    recipientVerified,
    connected,
    accountMismatch,
    isWalletDisconnected,
    isWrongNetwork,
    hasInputAmount,
    exceedsBalance,
    loadingAllowance,
    isUsdOutInvalid,
    walletReady,
  ]);

  const sendActionLabel = useMemo(() => {
    if (depositing) return 'Processing…';
    if (isWalletDisconnected) return 'Connect wallet';
    if (isWrongNetwork) return `Switch to ${targetName}`;
    if (connected && accountMismatch) return 'Reconnect wallet';
    if (loadingAllowance) return 'Checking approval…';
    if (exceedsBalance) return 'Exceeds balance';
    if (isUsdOutInvalid) return 'Amount too small';
    if (!hasInputAmount) return 'Enter an amount';
    if (needsUsdtApproval) return 'Approve USDT';
    return 'Send USD';
  }, [
    depositing,
    isWalletDisconnected,
    isWrongNetwork,
    connected,
    accountMismatch,
    targetName,
    loadingAllowance,
    exceedsBalance,
    isUsdOutInvalid,
    hasInputAmount,
    needsUsdtApproval,
  ]);

  async function handleSend() {
    if (depositing) return;
    if (swapInFlightRef.current) return;

    swapInFlightRef.current = true;
    setPostApprovalHint('');

    try {
      if (!phoneValid) {
        toast.error('Enter a valid 9-digit recipient phone number.');
        return;
      }
      if (!recipientVerified) {
        toast.error('Please verify the recipient number first.');
        return;
      }
      if (isWalletDisconnected) {
        toast.error('Connect MetaMask first.');
        return;
      }
      if (isWrongNetwork) {
        toast.error(`Switch MetaMask to ${targetName} using the network banner.`);
        return;
      }
      if (isReconnecting) {
        toast.error('Wallet is reconnecting. Please wait.');
        return;
      }
      if (accountMismatch) {
        toast.error('Your MetaMask account changed. Please reconnect your wallet and try again.');
        return;
      }
      if (!tokenOne || !tokenTwo) {
        toast.error('Payment configuration is missing. Please refresh.');
        return;
      }
      if (!isCryptoDepositToFiatPayout) {
        toast.error('This screen only supports crypto deposits with USD payout.');
        return;
      }

      const amountInNum = Number.parseFloat(String(tokenOneAmount || ''));
      if (!Number.isFinite(amountInNum) || amountInNum <= 0) {
        toast.error('Enter a valid amount.');
        return;
      }
      if (isUsdOutInvalid) {
        toast.error('Amount too small.');
        return;
      }
      if (exceedsBalance) {
        toast.error(`Insufficient ${tokenOne.ticker} balance in your wallet.`);
        return;
      }
      if (!address) {
        toast.error('Connect your wallet first.');
        return;
      }
      if (userStatus === 'blocked') {
        toast.error('Your account is temporarily blocked. Please contact support.');
        return;
      }

      const providerLive = await assertLiveProvider();
      if (!providerLive) return;

      const sessionReady = await ensureWalletSession();
      if (!sessionReady) return;

      const recipientPhoneForPayout = formatSomaliPhone(verifiedPhone);
      if (!recipientPhoneForPayout || recipientPhoneForPayout.length !== 12) {
        toast.error('Recipient phone number is missing. Please verify it again.');
        return;
      }

      const balanceCheck = checkSendUserBalance({
        tokenOne,
        tokenOneAmount,
        maxAmount,
        loadingMax,
      });
      if (!balanceCheck.ok) {
        toast.error(balanceCheck.message || 'Insufficient balance for this payment.');
        return;
      }

      if (isUsdtDeposit) {
        if (loadingAllowance) {
          toast.error('USDT approval is still being checked.');
          return;
        }
        if (allowanceError) {
          toast.error(allowanceError);
          return;
        }
      }

      if (!walletReady) {
        toast.error(
          'Wallet is not ready. Please reconnect MetaMask or switch to the correct network.'
        );
        return;
      }

      const ethGasCheck = await checkEthGasBalanceForCryptoDeposit({
        address,
        tokenOne,
        tokenOneAmount,
        publicClient,
      });
      if (!ethGasCheck?.hasEnoughEthForGas) {
        toast.error(ethGasCheck?.message || 'Insufficient ETH for network gas.');
        return;
      }

      if (needsUsdtApproval) {
        setDepositing(true);
        const approvalResult = await approveIfNeeded({
          tokenOne,
          amountIn: String(tokenOneAmount),
          address,
          chainId: targetChainId,
          precheckedAllowance: usdtAllowance?.toString?.() || null,
        });
        if (!approvalResult?.success) {
          toast.error(approvalResult?.message || 'USDT approval failed.');
          return;
        }
        setAllowanceRefreshNonce((n) => n + 1);
        setPostApprovalHint('Approved — tap Send USD to finish.');
        return;
      }

      const amountOutForRequest = String(usdOut || '0');

      setDepositing(true);

      const depositResult = await cryptoStrategy({
        name: recipientName,
        address,
        tokenOne,
        tokenTwo,
        tokenOneAmount: String(tokenOneAmount),
        tokenTwoAmount: amountOutForRequest,
        slippage: SEND_SLIPPAGE,
        NETWORK_ID_DEC: targetChainId,
        networkFee: '0',
        recipientPhone: recipientPhoneForPayout,
        deposit: depositForStrategy,
        handleContractError,
      });

      if (!depositResult?.success) {
        if (isExpiredQuoteMessage(depositResult?.message)) {
          setUsdToSend('');
          setTokenOneAmount('');
        }
        return;
      }

      resetSwapInput();
      resetField('recipientPhone');
      setRecipientCheckStatus('idle');
      setRecipientCheckMessage('');
      setVerifiedPhone('');
      setRecipientName('');
    } catch (err) {
      console.error('[handleSend failed]', err);
      const message = handleContractError(err);
      if (isExpiredQuoteMessage(message)) {
        setUsdToSend('');
        setTokenOneAmount('');
      }
      toast.error(message);
    } finally {
      swapInFlightRef.current = false;
      setDepositing(false);
    }
  }

  // ---- Fund handlers -----------------------------------------------------
  const handleOpenLogin = async () => {
    setLoginModalKey((k) => k + 1);
    setHasOpenedLogin(true);
    setOpenLogin(true);
    await prepareAuthRequests?.();
  };
  const handleCloseLogin = () => setOpenLogin(false);

  function handleUsdChange(e) {
    const { value } = e.target;
    setPostApprovalHint('');
    if (value === '') {
      resetSwapInput();
      return;
    }
    if (!/^\d*(\.\d{0,2})?$/.test(value)) return;
    setUsdToSend(value);
  }

  function handleMaxAmount() {
    if (!tokenOne || tokenOne?.type === 'fiat') return;
    if (loadingMax || !maxAmount) return;
    if (!priceIn || priceIn <= 0) return;

    // maxAmount from useTokenOneMax already has the ETH gas reserve removed —
    // do not subtract another buffer here.
    const maxTokens = Number.parseFloat(String(maxAmount));
    if (!Number.isFinite(maxTokens) || maxTokens <= 0) return;

    const grossUsd = maxTokens * priceIn;
    const netUsd = grossUsd * (1 - feeRate);
    if (!Number.isFinite(netUsd) || netUsd <= 0) return;

    // Tiny epsilon so the USD -> token round-trip can never land a few wei
    // above maxAmount (which would flip the button to "Exceeds balance").
    const flooredNetUsd = Math.floor(netUsd * 100 - 1e-6) / 100;
    if (flooredNetUsd <= 0) return;

    setPostApprovalHint('');
    setUsdToSend(flooredNetUsd.toFixed(2));
  }

  function handleFundAmountChange(e) {
    const { value } = e.target;
    if (value === '') {
      setFundAmount('');
      return;
    }
    if (!/^\d*(\.\d{0,2})?$/.test(value)) return;
    setFundAmount(value);
  }

  function getActiveDestination() {
    if (destMode === 'connect') {
      if (connected && address) return { address, valid: true, message: '' };
      return { address: '', valid: false, message: '' };
    }
    const check = validateDestination(
      { network: receiveAsset?.ticker, key: receiveAsset?.ticker },
      manualAddress
    );
    return { address: manualAddress.trim(), valid: check.valid, message: check.message };
  }

  // Fund step 1: validate, build the preview, open the USSD/QR pay dialog.
  function handleFundSubmit() {
    const num = Number.parseFloat(String(fundAmount || ''));
    if (!Number.isFinite(num) || num <= 0) {
      toast.error('Enter an amount.');
      return;
    }
    if (!fundQuote) {
      toast.error('Rate is still loading. Please wait.');
      return;
    }
    if (!receiveAsset?.ticker) {
      toast.error('Select an asset to receive.');
      return;
    }
    if (!user?.phoneNumber) {
      toast.error('Phone number missing. Please log in again.');
      return;
    }

    const dest = getActiveDestination();
    if (!dest.address) {
      toast.error('Add a destination wallet.');
      return;
    }
    if (!dest.valid) {
      toast.error(dest.message || 'Invalid destination address.');
      return;
    }

    // Hold the destination for the confirm step (dialog doesn't carry it).
    fundDestinationRef.current = dest.address;

    const sahalAccount = String(import.meta.env.VITE_SAHAL_ACC || '').trim();

    setFundClickedAck(false);
    setFundPreview({
      recipient: sahalAccount,
      amount: formatAmount(fundAmount),
      currency: 'usd',
      userPhone: user?.phoneNumber || '',
      providerKey: null,
    });
    openFundDialog.onTrue();
  }

  function handleFundDialogClose() {
    setFundClickedAck(false);
    setFundPreview(null);
    openFundDialog.onFalse();
  }

  // Fund step 2: user acknowledged payment -> create the fiat intent.
  async function handleConfirmFundPayment() {
    if (submitting) return;

    try {
      if (!user?.phoneNumber) {
        toast.error('Phone number missing. Please log in again.');
        handleFundDialogClose();
        return;
      }
      if (!fundQuote) {
        toast.error('Rate is no longer available. Please try again.');
        handleFundDialogClose();
        return;
      }

      const destinationAddress = fundDestinationRef.current;
      if (!destinationAddress) {
        toast.error('Destination wallet is missing. Please re-enter it.');
        handleFundDialogClose();
        return;
      }

      setSubmitting(true);

      // Send exactly the fields the /swaps/fiat-intent schema accepts (the
      // schema rejects unknown keys, e.g. the old `tokenTwo` object / `direction`).
      const result = await createExchangePayoutIntent({
        clientRequestId: newClientRequestId(),
        amountIn: String(fundAmount),
        tokenOut: String(receiveAsset?.coingeckoId || receiveAsset?.ticker || '').trim(),
        amountOut: toPlainAmountString(fundQuote?.cryptoOut, receiveAsset?.decimals),
        recipientAddress: destinationAddress,
        chainId: targetChainId,
        phoneNumber: user.phoneNumber,
        networkFee: '0',
        slippage: SEND_SLIPPAGE,
      });

      if (!result?.success) {
        if (result?.code === 'QUOTE_CHANGED') {
          handleFundDialogClose();
          toast.error('The quote changed. Please review the new amount and try again.');
          return;
        }
        toast.error(result?.message || 'Could not start the top-up.');
        return;
      }

      handleFundDialogClose();
      setFundAmount('');
      toast.success('Payment received — awaiting confirmation.');
    } catch (err) {
      console.error('[handleConfirmFundPayment failed]', err);
      toast.error(handleContractError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function getFundDisabledReason() {
    const num = Number.parseFloat(String(fundAmount || ''));
    if (!Number.isFinite(num) || num <= 0) return 'Enter an amount';
    if (!fundQuote) return 'Fetching rate…';
    const dest = getActiveDestination();
    if (!dest.address) return 'Add a destination wallet';
    if (!dest.valid) return dest.message || 'Invalid address';
    if (submitting) return 'Processing…';
    return '';
  }

  // ---- render helpers ----------------------------------------------------
  function renderTabs() {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          p: 0.5,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          mb: 2,
        }}
      >
        <Box sx={getTabSx(activeTab === 'send')} onClick={() => setActiveTab('send')}>
          Send to Somalia
        </Box>
        <Box sx={getTabSx(activeTab === 'fund')} onClick={() => setActiveTab('fund')}>
          Fund my wallet
        </Box>
      </Stack>
    );
  }

  function getRecipientHelperColor() {
    if (recipientVerified) return UI.green;
    if (recipientUnavailable) return UI.danger;
    if (recipientErrored) return UI.yellow;
    return UI.textMuted;
  }

  function renderCheckButtonLabel() {
    if (checkingRecipient) return <CircularProgress size={16} sx={{ color: '#0A0B08' }} />;
    return 'Check';
  }

  function renderCheckOrConnect() {
    if (!connected) {
      return (
        <Box sx={{ minWidth: 150, display: 'flex' }}>
          <WalletConnectButtonClient green fullWidth />
        </Box>
      );
    }
    return (
      <Button
        onClick={handleVerifyRecipient}
        disabled={checkingRecipient}
        sx={{ ...gradientButtonSx, width: 'auto', px: 2.5, height: 'auto' }}
      >
        {renderCheckButtonLabel()}
      </Button>
    );
  }

  function getVerifyFootnote() {
    if (connected) return 'Wallet connected. Check the number to continue.';
    return 'Connect your wallet to verify the recipient.';
  }

  function renderQuoteRowWithIcon(iconName, label, value) {
    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Iconify icon={iconName} width={14} sx={{ color: UI.textMuted }} />
          <Typography sx={{ fontSize: 12.5, color: UI.textMuted }}>{label}</Typography>
        </Stack>
        {value}
      </Stack>
    );
  }

  function renderRateValue(rateStr) {
    if (rateLoading) {
      return (
        <Box
          sx={{
            width: 96,
            height: 12,
            borderRadius: 1,
            bgcolor: 'rgba(255,255,255,0.06)',
            animation: 'pulse 1.4s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.4 },
              '50%': { opacity: 0.9 },
            },
          }}
        />
      );
    }
    return (
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: UI.text }}>{rateStr}</Typography>
    );
  }

  function renderFeeValue() {
    const feeText = formatFeePercent(profitLoaded, profitPercent, feeUsd);
    return (
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: UI.text }}>{feeText}</Typography>
    );
  }

  function renderPlainValue(text) {
    return <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: UI.text }}>{text}</Typography>;
  }

  function renderVerifyView() {
    const helperColor = getRecipientHelperColor();
    const payLabel = getPayWithLabel();

    let verifyRateStr = `1 ${payLabel} = —`;
    if (rate) {
      verifyRateStr = `1 ${payLabel} = ${formatUsd(rate)}`;
    }

    return (
      <Stack spacing={5}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: UI.text, lineHeight: 1.2 }}>
            Send money to Somalia
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: UI.textMuted, lineHeight: 1.5 }}>
            Pay from your wallet, they receive mobile money in minutes.
          </Typography>
        </Box>
        <Box>
          <MonoLabel>Recipient phone number</MonoLabel>
          <Controller
            name="recipientPhone"
            control={control}
            render={({ field, fieldState }) => {
              const onChangeDigits = (event) => {
                const onlyDigits = event.target.value.replace(/\D/g, '').slice(0, 9);
                field.onChange(onlyDigits);
                clearErrors('recipientPhone');
                setRecipientCheckStatus('idle');
                setRecipientCheckMessage('');
                setVerifiedPhone('');
              };
              const onKeyDownField = (event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                if (phoneLocked || checkingRecipient || !connected) return;
                handleVerifyRecipient();
              };

              const helperText = fieldState.error?.message || recipientCheckMessage || '';

              return (
                <Box sx={{ mt: 0.75 }}>
                  <Stack direction="row" spacing={1}>
                    <Box
                      sx={{ ...fieldBoxSx, flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: UI.textMuted }}>
                        +252
                      </Typography>
                      <InputBase
                        value={field.value}
                        onChange={onChangeDigits}
                        onKeyDown={onKeyDownField}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        placeholder="61 234 5678"
                        inputProps={{ inputMode: 'numeric', maxLength: 9 }}
                        fullWidth
                        sx={{
                          fontSize: 15,
                          color: UI.text,
                          '& input::placeholder': { color: UI.textFaint, opacity: 1 },
                        }}
                      />
                    </Box>
                    {renderCheckOrConnect()}
                  </Stack>
                  {helperText && (
                    <Typography sx={{ mt: 0.75, fontSize: 12, color: helperColor }}>
                      {helperText}
                    </Typography>
                  )}
                </Box>
              );
            }}
          />
        </Box>

        <Stack spacing={0.9} sx={{ pt: 1.5, borderTop: `1px solid ${UI.border}` }}>
          {renderQuoteRowWithIcon('solar:tag-price-bold', 'Rate', renderRateValue(verifyRateStr))}
          {renderQuoteRowWithIcon('solar:wallet-money-bold', 'Fee', renderFeeValue())}
          {renderQuoteRowWithIcon(
            'solar:clock-circle-bold',
            'Arrives',
            renderPlainValue('Under 10 min')
          )}
        </Stack>

        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              bgcolor: 'rgba(127,212,38,0.06)',
              border: `1px solid rgba(127,212,38,0.18)`,
            }}
          >
            <Iconify icon="solar:shield-check-bold" width={16} sx={{ color: UI.green }} />
            <Typography sx={{ fontSize: 11.5, color: UI.textMuted, lineHeight: 1.4 }}>
              We&apos;ll show the recipient&apos;s name to confirm before anything moves.
            </Typography>
          </Stack>
          <Typography sx={{ textAlign: 'center', fontSize: 11.5, color: UI.textFaint }}>
            {getVerifyFootnote()}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  function renderSendActionContent() {
    if (depositing) return <CircularProgress size={18} sx={{ color: '#0A0B08' }} />;
    return sendActionLabel;
  }

  function renderSendActionButton() {
    if (isWalletDisconnected) {
      return <WalletConnectButtonClient green fullWidth />;
    }
    return (
      <Button onClick={handleSend} disabled={isSendDisabled} sx={gradientButtonSx}>
        {renderSendActionContent()}
      </Button>
    );
  }

  function getPayWithLabel() {
    if (tokenOne?.ticker) return tokenOne.ticker;
    if (payWith === 'eth') return 'ETH';
    return 'USDT';
  }

  function renderBalanceLine() {
    const label = getPayWithLabel();
    if (loadingMax) return `Balance: … ${label}`;
    return `Balance: ${formatAmount(maxAmount ?? '0')} ${label}`;
  }

  function renderYouPayValue() {
    const label = getPayWithLabel();
    if (tokenOneAmount) return `${formatAmount(tokenOneAmount)} ${label}`;
    return `0.00 ${label}`;
  }

  function renderMaxButton() {
    const disabled = loadingMax || !maxAmount || !priceIn;
    return (
      <Box
        onClick={() => {
          if (disabled) return;
          handleMaxAmount();
        }}
        sx={{
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: 0.5,
          borderRadius: 1,
          px: 0.85,
          py: 0.25,
          userSelect: 'none',
          transition: 'background-color 0.15s ease',
          ...getMaxButtonSx(disabled),
        }}
      >
        MAX
      </Box>
    );
  }

  function renderAmountView() {
    const payLabel = getPayWithLabel();

    let sendRateStr = `1 ${payLabel} = —`;
    if (rate) {
      sendRateStr = `1 ${payLabel} = ${formatUsd(rate)}`;
    }

    let youPayColor = UI.green;
    if (exceedsBalance) youPayColor = UI.danger;

    return (
      <Stack spacing={1.5}>
        <Box
          sx={{
            ...fieldBoxSx,
            border: `1px solid rgba(127,212,38,0.3)`,
            bgcolor: 'rgba(127,212,38,0.05)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                bgcolor: 'rgba(127,212,38,0.15)',
                color: UI.green,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              {String(recipientName || '?')
                .slice(0, 2)
                .toUpperCase()}
            </Box>
            <Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: UI.text }}>
                  {recipientName}
                </Typography>
                <Iconify icon="solar:verified-check-bold" width={15} sx={{ color: UI.green }} />
              </Stack>
              <Typography sx={{ fontSize: 12, color: UI.textMuted }}>
                +252 {recipientPhone}
              </Typography>
            </Box>
          </Stack>
          <Box
            onClick={handleChangeRecipient}
            sx={{
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 700,
              color: UI.green,
              border: `1px solid rgba(127,212,38,0.3)`,
              borderRadius: 1.25,
              px: 1.25,
              py: 0.5,
              '&:hover': { bgcolor: 'rgba(127,212,38,0.1)' },
            }}
          >
            Change
          </Box>
        </Box>

        <Box>
          <MonoLabel>Pay with</MonoLabel>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ mt: 0.75, p: 0.4, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.03)' }}
          >
            <Box
              sx={{ ...getSegmentSx(payWith === 'usdt'), flex: 1, textAlign: 'center' }}
              onClick={() => {
                if (!usdtToken) return;
                setPayWith('usdt');
              }}
            >
              USDT
            </Box>
            <Box
              sx={{ ...getSegmentSx(payWith === 'eth'), flex: 1, textAlign: 'center' }}
              onClick={() => {
                if (!ethToken) return;
                setPayWith('eth');
              }}
            >
              ETH
            </Box>
          </Stack>
        </Box>

        <Box sx={fieldBoxSx}>
          <MonoLabel>Recipient receives (USD)</MonoLabel>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
            <Typography sx={{ fontSize: 30, fontWeight: 700, color: UI.textMuted }}>$</Typography>
            <InputBase
              value={usdToSend}
              onChange={handleUsdChange}
              placeholder="0.00"
              inputProps={{ inputMode: 'decimal' }}
              fullWidth
              sx={{
                fontSize: 30,
                fontWeight: 700,
                color: UI.text,
                '& input::placeholder': { color: UI.textFaint, opacity: 1 },
              }}
            />
          </Stack>
        </Box>

        <Box sx={fieldBoxSx}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <MonoLabel>You pay</MonoLabel>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontSize: 11, color: UI.textFaint }}>
                {renderBalanceLine()}
              </Typography>
              {renderMaxButton()}
            </Stack>
          </Stack>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: youPayColor, mt: 0.5 }}>
            {renderYouPayValue()}
          </Typography>
          {exceedsBalance && (
            <Typography sx={{ mt: 0.4, fontSize: 11.5, color: UI.danger }}>
              Exceeds your wallet balance.
            </Typography>
          )}
        </Box>

        <Stack spacing={0.75} sx={{ px: 0.5, py: 0.5 }}>
          {renderQuoteRowWithIcon('solar:tag-price-bold', 'Rate', renderRateValue(sendRateStr))}
          {renderQuoteRowWithIcon('solar:wallet-money-bold', 'Fee', renderFeeValue())}
          {renderQuoteRowWithIcon(
            'solar:clock-circle-bold',
            'Arrives',
            renderPlainValue('Under 10 min')
          )}
        </Stack>

        {renderSendActionButton()}

        {postApprovalHint && (
          <Typography sx={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: UI.green }}>
            {postApprovalHint}
          </Typography>
        )}

        <Typography sx={{ textAlign: 'center', fontSize: 11.5, color: UI.textFaint }}>
          You approve the payment in your wallet. We pay the recipient once it confirms.
        </Typography>
      </Stack>
    );
  }

  function renderSendPanel() {
    if (recipientVerified) return renderAmountView();
    return renderVerifyView();
  }

  function renderFundPrimaryAction() {
    if (loading) {
      return (
        <Button disabled sx={gradientButtonSx}>
          Loading…
        </Button>
      );
    }
    if (!isPhoneSession) {
      return (
        <Button onClick={handleOpenLogin} startIcon={<WhatsAppIcon />} sx={gradientButtonSx}>
          Log in with WhatsApp
        </Button>
      );
    }
    const reason = getFundDisabledReason();
    if (reason) {
      return (
        <Button disabled sx={gradientButtonSx}>
          {reason}
        </Button>
      );
    }
    return (
      <Button onClick={handleFundSubmit} sx={gradientButtonSx}>
        Continue
      </Button>
    );
  }

  function renderDestinationConnect() {
    if (connected && address) {
      return (
        <Box
          sx={{
            ...fieldBoxSx,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: UI.green }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: UI.text }}>
              {shortenAddress(address)}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, color: UI.textMuted }}>Auto-filled</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ '& button, & > *': { width: '100%' } }}>
        <WalletConnectButtonClient />
      </Box>
    );
  }

  function renderDestinationManual() {
    const check = validateDestination(
      { network: receiveAsset?.ticker, key: receiveAsset?.ticker },
      manualAddress
    );
    const showError = manualAddress.trim().length > 0 && !check.valid;
    return (
      <Box>
        <Box sx={fieldBoxSx}>
          <InputBase
            value={manualAddress}
            onChange={(e) => setManualAddress(e.target.value)}
            placeholder={`Paste ${receiveAsset?.ticker || ''} address`}
            fullWidth
            sx={{
              fontSize: 14,
              color: UI.text,
              '& input::placeholder': { color: UI.textFaint, opacity: 1 },
            }}
          />
        </Box>
        {showError && (
          <Typography sx={{ mt: 0.75, fontSize: 12, color: UI.danger }}>{check.message}</Typography>
        )}
      </Box>
    );
  }

  function renderDestSegment(mode, label, iconName) {
    const active = destMode === mode;

    return (
      <Box
        onClick={() => setDestMode(mode)}
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          py: 1.1,
          px: 2,
          borderRadius: 1.25,
          cursor: 'pointer',
          fontSize: 12.5,
          transition: 'color 0.18s ease, font-weight 0.18s ease',
          userSelect: 'none',
          ...getDestSegmentSx(active),
        }}
      >
        <Iconify icon={iconName} width={15} />
        {label}
      </Box>
    );
  }

  function renderDestinationToggle() {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          p: 0.4,
          borderRadius: 1.75,
          bgcolor: 'rgba(255,255,255,0.03)',
          border: `1px solid ${UI.border}`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            bottom: 4,
            left: 4,
            width: 'calc(50% - 4px)',
            borderRadius: 1.25,
            bgcolor: 'rgba(127,212,38,0.14)',
            border: `1px solid rgba(127,212,38,0.35)`,
            transform: getDestIndicatorTransform(destMode),
            transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'none',
          }}
        />
        {renderDestSegment('connect', 'Connect', 'solar:link-bold')}
        {renderDestSegment('manual', 'Enter address', 'solar:pen-bold')}
      </Box>
    );
  }

  function renderDestination() {
    if (destMode === 'connect') return renderDestinationConnect();
    return renderDestinationManual();
  }

  function renderFundPanel() {
    function getFundRateLine() {
      if (fundQuote) return `1 ${receiveAsset?.ticker} = ${formatUsd(fundQuote.price)}`;
      return `1 ${receiveAsset?.ticker || ''} = —`;
    }
    function getFundFeeLine() {
      return formatFeePercent(profitLoaded, profitPercent, fundQuote?.fee);
    }
    function getFundReceiveLine() {
      if (fundQuote) return `${formatCryptoAmount(fundQuote.cryptoOut)} ${receiveAsset?.ticker}`;
      return `0 ${receiveAsset?.ticker || ''}`;
    }
    const rateLine = getFundRateLine();
    const feeLine = getFundFeeLine();
    const receiveLine = getFundReceiveLine();

    return (
      <Stack spacing={1.5}>
        <Box sx={fieldBoxSx}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <MonoLabel>You pay</MonoLabel>
            <MonoLabel>Local mobile money</MonoLabel>
          </Stack>
          <Stack direction="row" alignItems="center" sx={{ mt: 0.75 }} spacing={1}>
            <Typography sx={{ fontSize: 26, fontWeight: 700, color: UI.textMuted }}>$</Typography>
            <InputBase
              value={fundAmount}
              onChange={handleFundAmountChange}
              placeholder="0"
              inputProps={{ inputMode: 'decimal' }}
              fullWidth
              sx={{
                fontSize: 34,
                fontWeight: 700,
                color: UI.text,
                '& input::placeholder': { color: UI.textFaint, opacity: 1 },
              }}
            />
          </Stack>
        </Box>

        <Box sx={fieldBoxSx}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <MonoLabel>You receive</MonoLabel>
            <MonoLabel>Asset</MonoLabel>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 0.75 }}
          >
            <Typography sx={{ fontSize: 26, fontWeight: 700, color: UI.green }}>
              {receiveLine}
            </Typography>
            <SelectorButton
              primary={receiveAsset?.ticker || '—'}
              onClick={(e) => setFundAssetAnchor(e.currentTarget)}
            />
          </Stack>
        </Box>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <MonoLabel>Destination wallet</MonoLabel>
          </Stack>
          {renderDestinationToggle()}
          <Box sx={{ mt: 1 }}>{renderDestination()}</Box>
        </Box>

        <Stack spacing={0.75} sx={{ px: 0.5, py: 0.5 }}>
          {renderQuoteRowWithIcon('solar:tag-price-bold', 'Rate', renderPlainValue(rateLine))}
          {renderQuoteRowWithIcon('solar:wallet-money-bold', 'Fee', renderPlainValue(feeLine))}
          {renderQuoteRowWithIcon(
            'solar:clock-circle-bold',
            'Arrives',
            renderPlainValue('After network confirmation')
          )}
        </Stack>

        {renderFundPrimaryAction()}

        <Typography sx={{ textAlign: 'center', fontSize: 11.5, color: UI.textFaint }}>
          Log in once, then reuse your linked wallet on your next top-up.
        </Typography>
      </Stack>
    );
  }

  function renderActivePanel() {
    if (activeTab === 'send') return renderSendPanel();
    return renderFundPanel();
  }

  const loginFallback = () => {
    if (!openLogin) return null;
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1300,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <Typography sx={{ color: UI.text }}>Loading…</Typography>
      </Box>
    );
  };

  return (
    <>
      {hasOpenedLogin && (
        <Suspense fallback={loginFallback()}>
          <LoginModal key={loginModalKey} open={openLogin} onClose={handleCloseLogin} />
        </Suspense>
      )}

      {openFundDialog.value && (
        <Suspense fallback={null}>
          <FiatPayoutConfirmDialog
            client
            open={openFundDialog.value}
            onClose={handleFundDialogClose}
            onConfirm={handleConfirmFundPayment}
            loading={submitting}
            preview={fundPreview}
            isMobile={isMobile}
            clickedProcessB={fundClickedAck}
            setClickedProcessB={setFundClickedAck}
          />
        </Suspense>
      )}

      <Box sx={cardSx}>
        {renderTabs()}
        {renderActivePanel()}
      </Box>
    </>
  );
}
